import { selectIceRelayRequired } from 'features/application/applicationSlice';
import { SFrameManager } from 'features/e2ee/SFrameManager';
import {
  receivingPluginCleanup,
  remoteTrackReceived,
  streamingError,
  videoroomError,
} from 'features/streaming/actions';
import {
  Mountpoint,
  StreamingErrorMessage,
  SubscriberMid,
  SubscribeStreamList,
} from 'features/streaming/types';
import Janus, { JanusJS } from 'lib/janus';
import { store } from 'store/store';
import { noop } from 'utils/flow';
import { formatErrorForLogging, logger } from 'utils/logger';
import { ControlledReceivingHandle } from 'utils/webrtc/ControlledReceivingHandle';
import { GENERIC_JANUS_ERROR } from 'utils/webrtc/errors';
import { RTCClient, ToggleMediaTemplate } from 'utils/webrtc/index';
import {
  batchToggleMediaTemplate,
  sendMessage,
  startSubscriptionTemplate,
  watchRestartTemplate,
  watchRoomTemplate,
} from 'utils/webrtc/messages';
import { BaseReceiver } from 'utils/webrtc/receiving/BaseReceiver';
import { refreshSubscribeGrant } from 'utils/webrtc/refreshSubscribeGrant';
import {
  isSubscribeGrantRefusal,
  SUBSCRIBE_GRANT_REFUSED_CODE,
} from 'utils/webrtc/subscribeGrantError';
import {
  emitStreamingRestartRefused,
  StreamingRestartRefusal,
} from 'utils/webrtc/subscribeTelemetry';
import { JanusConnection } from 'utils/webrtc/types';
import { handleAttachError } from 'utils/webrtc/handleAttachError';
import { logJanusHandleAttached } from 'utils/webrtc/logJanusHandleAttached';

export class StreamingReceiver extends BaseReceiver {
  pluginHandle: string = '';

  trackPool: Record<SubscriberMid, MediaStreamTrack> = {};

  private localStreams: ToggleMediaTemplate[] = [];

  private pendingResubscription: ToggleMediaTemplate = [];

  // A plugin refusal arrives as an event, not a rejection, so only this ties an error to the restart `watch`.
  private restartWatchInFlight = false;

  // Rebuilds re-attach under the same `pluginHandle` — it is the Redux key for
  // this feed's tracks — so only the attach itself tells a superseded handle from the live one.
  private attachGeneration = 0;

  attachPlugin = (pluginHandle: string, connection: JanusConnection) => {
    this.connection = connection;
    this.pluginHandle = pluginHandle;
    this.attachGeneration += 1;
    this.onNewPeerConnection();

    const generation = this.attachGeneration;
    const forActiveHandle =
      <A extends unknown[]>(handler: (...args: A) => void) =>
      (...args: A) => {
        if (this.attachGeneration === generation) {
          handler(...args);
        }
      };

    connection.janus.attach({
      plugin: 'janus.plugin.streaming',
      opaqueId: connection.handle,
      success: async (plugin) => {
        if (this.attachGeneration !== generation) {
          plugin.detach();
          return;
        }

        this.plugin = new ControlledReceivingHandle(plugin, pluginHandle, connection);

        logJanusHandleAttached('streaming-subscriber', plugin, connection.janus);

        try {
          await this.watchRoomStreams();
        } catch (error) {
          if (
            this.attachGeneration !== generation ||
            RTCClient.supressErrors ||
            this.feed !== RTCClient.receivingFeed
          ) {
            return;
          }

          logger
            .remote({ system: true, capture: 'streaming' })
            .error('[Streaming Receiver] watch refused', formatErrorForLogging(error));
          this.reportMediaFailure('connection_failed', 'new', {
            previouslyConnected: this.iceRecovery.hasConnected,
          });
        }
      },
      error: handleAttachError('attach_streaming_feed'),
      consentDialog: noop,
      onmessage: forActiveHandle(this.onMessage),
      onlocaltrack: noop,
      onremotetrack: forActiveHandle((track, mid, on) => {
        if (on) {
          if (this.feed.feedIdByMid[mid]) {
            store.dispatch(remoteTrackReceived({ pluginHandle, track, mid }));
          }

          this.trackPool[mid] = track;
        } else {
          // @TODO monitor this
          // ❌ BUG: on=false is not handled - this path should clean up audio elements
          const feedId = this.feed.feedIdByMid[mid];
          const hasAudioElement = !!this.feed.audioElements[mid];

          // Only log audio tracks that have elements (potential audio loss)
          if (track.kind === 'audio' && hasAudioElement) {
            logger
              .remote({ system: true, capture: 'streaming' })
              .error(
                '[Streaming Receiver] Audio track removed by Janus but element not cleaned up',
                {
                  mid,
                  feedId,
                  trackId: track.id,
                  elementId: this.feed.audioElements[mid]?.id,
                }
              );
          }
        }
      }),
      oncleanup: forActiveHandle(() => {
        this.onPeerConnectionGone();
        this.pendingResubscription = [];
        store.dispatch(receivingPluginCleanup({ pluginHandle, instanceId: this.feed.instanceId }));
      }),
      webrtcState: forActiveHandle((on, reason) => {
        Janus.log(`[Streaming Receiver] WebRTC PeerConnection is ${on ? 'up' : 'down'} now`);
        if (on) {
          this.onWebRTCConnectionUp();
        } else {
          this.onWebRTCConnectionDown(reason);
        }
      }),
      iceState: forActiveHandle(this.onIceState),
      iceGatheringState: forActiveHandle(this.onIceGatheringState),
      connectionState: forActiveHandle(this.onConnectionState),
      slowLink: (uplink, lost, mid) => {
        Janus.warn(
          `Janus reports problems (${
            uplink ? 'sending' : 'receiving'
          }) packets on mid ${mid} (${lost} lost packets)`
        );
      },
      sframe: SFrameManager.getClient(),
    });
  };

  private onMessage = (message: JanusJS.EvtMessage, messageJsep?: JanusJS.JSEP) => {
    const feed = this.plugin;

    if (!feed) {
      return;
    }

    logger
      .remote({ system: true, capture: 'streaming', tier: 1 })
      .debug(`[Streaming Receiver] Got a remote message in receiving feed:`, message);
    if (messageJsep) {
      feed.janusPlugin.createAnswer({
        jsep: messageJsep,
        tracks: [
          { type: 'audio', recv: true },
          { type: 'video', recv: true },
        ],
        // Gate on the live SFrame client, not the E2EE flag: no decrypt pump means zero RTP.
        externalEncryption: !!SFrameManager.getClient(),
        // This answer creates the handle's only PC, so a rebuild after a DTLS stall has one
        // chance to skip the direct path that just failed.
        iceTransportPolicy: selectIceRelayRequired(store.getState()) ? 'relay' : undefined,
        success: async (jsep: JanusJS.JSEP) => {
          await sendMessage(feed.janusPlugin, startSubscriptionTemplate(RTCClient.roomId!), jsep);
        },
        error: GENERIC_JANUS_ERROR('receiving_feed'),
      });
    }

    if (message.result?.status) {
      this.restartWatchInFlight = false;

      logger
        .remote({ system: true, capture: 'streaming' })
        .info('Streaming receiver status =', message.result?.status);

      if (message.result?.status === 'started') {
        this.stopQueuedLocalStreams();
        this.restorePendingSubscriptions();
      }

      return;
    }

    if (message.result) {
      const event = message.streaming;

      switch (event) {
        case 'event': {
          // @ts-ignore
          const { substream, temporal, mid } = message.result;

          if (
            (substream !== null && substream !== undefined) ||
            (temporal !== null && temporal !== undefined)
          ) {
            logger.debug('[Streaming Receiver] Simulcast substream update: ', substream);
            if (temporal) {
              logger.debug('[Streaming Receiver] Simulcast temporal update: ', temporal);
            }
            this.feed.updateSimulcastValue(mid, substream, temporal);
          } else if (message.error_code) {
            store.dispatch(
              videoroomError({
                handle: this.plugin!.handle,
                message: { error_code: message.error_code, error: String(message.error ?? '') },
                source: 'streaming-receiver',
              })
            );
          } else {
            const messageStr = '[Streaming Receiver] Got an anomalous message in receiving feed';

            logger.remote({ system: true, capture: 'streaming' }).warn(messageStr, message);
          }
          break;
        }
        case 'updated': {
          // all good;
          break;
        }
        default:
          logger
            .remote({ system: true, capture: 'streaming' })
            .warn('[Streaming Receiver] Got an anomalous message in receiving feed', message);
      }
    } else if (message.error) {
      // A refused restart is not a missing room.
      if (this.restartWatchInFlight) {
        this.restartWatchInFlight = false;
        this.refuseRestart(feed, 'plugin', message.error_code ?? null);
        return;
      }

      store.dispatch(streamingError(message as unknown as StreamingErrorMessage));
    }
  };

  subscribe = async (connectionHandle: string, streams: SubscribeStreamList) => {
    const configureStreams: ToggleMediaTemplate = [];

    streams.forEach((stream) => {
      if (stream.mid) {
        const hadPreviousMapping = !!this.feed.feedIdByMid[stream.mid];
        const previousFeedId = this.feed.feedIdByMid[stream.mid];

        this.feed.feedIdByMid[stream.mid] = stream.feed;

        // yes, double stream.mid here is valid because in streaming pub and sub mid are identical;
        this.feed.media.setSubscriberMid(stream.feed, stream.mid, stream.mid);

        // Flush out-of-order tracks from pool when mapping arrives
        if (this.trackPool[stream.mid]) {
          store.dispatch(
            remoteTrackReceived({
              pluginHandle: this.plugin!.handle,
              track: this.trackPool[stream.mid],
              mid: stream.mid,
            })
          );
        }

        // Warn about mid reassignments (should not happen normally)
        if (hadPreviousMapping && previousFeedId !== stream.feed) {
          logger
            .remote({ system: true, capture: 'streaming' })
            .warn('[Streaming Receiver] Mid reassigned to different feed', {
              mid: stream.mid,
              oldFeed: previousFeedId,
              newFeed: stream.feed,
            });
        }

        configureStreams.push({ mid: stream.mid, send: true, ...(stream.options || {}) });
      }
    });

    if (configureStreams.length && this.plugin?.janusPlugin) {
      sendMessage(this.plugin.janusPlugin, batchToggleMediaTemplate(configureStreams));
    }
  };

  watchRoomStreams = async () => {
    if (RTCClient.mountpointId) {
      await sendMessage(
        this.plugin!.janusPlugin,
        watchRoomTemplate(RTCClient.mountpointId, RTCClient.roomPin!)
      );
    }
  };

  list: () => Promise<{ data: { list: Mountpoint[] } }> = () =>
    sendMessage(this.plugin!.janusPlugin, { request: 'list' });

  cf: (a: any) => Promise<{ list: Mountpoint[] }> = () =>
    // @ts-ignore
    sendMessage(this.plugin!.janusPlugin, batchToggleMediaTemplate(a));

  unsubscribe = () => {
    // this is a stub; currently streaming receiver doesn't unsubscribe
    // since we're always connected to all tracks from a mountpoint
    // in future for pagination we'll need to send configure request here probably
  };

  stopQueuedLocalStreams = () => {
    if (this.localStreams.length) {
      this.localStreams.forEach((streams) => {
        this.stopLocalStreams(streams);
      });

      this.localStreams = [];
    }
  };

  stopLocalStreams = (streams: ToggleMediaTemplate) => {
    if (this.plugin?.janusPlugin) {
      sendMessage(this.plugin!.janusPlugin, batchToggleMediaTemplate(streams));
    } else {
      this.localStreams.push(streams);
    }
  };

  private restorePendingSubscriptions = () => {
    if (!this.pendingResubscription.length) {
      return;
    }

    logger
      .remote({ system: true, capture: 'streaming' })
      .info('[Streaming Receiver] Restoring subscriptions', {
        midsCount: this.pendingResubscription.length,
      });

    if (this.plugin?.janusPlugin) {
      sendMessage(this.plugin.janusPlugin, batchToggleMediaTemplate(this.pendingResubscription));
    }

    this.pendingResubscription = [];
  };

  // The wedged handle is dead server-side: detach it, or its late hangup tears the replacement
  // down. The mountpoint `watch` always renegotiates, so there is no "nothing to rebuild" case.
  rebuildHandler: NonNullable<BaseReceiver['rebuildHandler']> = async (feed) => {
    this.pendingResubscription = Object.keys(this.feed.feedIdByMid).map((mid) => ({
      mid,
      send: true,
    }));

    // Gate the wedged handle out before the grant round-trip: on a Janus hangup its `oncleanup`
    // lands during that await and would otherwise wipe the queued mids and cancel the deadline.
    const wedgedGeneration = this.attachGeneration;
    this.attachGeneration += 1;

    // The `watch` spends a single-use grant, so mint a fresh one right before the attach that sends it.
    try {
      await refreshSubscribeGrant();
    } catch (error) {
      this.attachGeneration = wedgedGeneration;
      throw error;
    }

    this.attachPlugin(this.pluginHandle, feed.connection);
    feed.janusPlugin.detach();

    return true;
  };

  // Each grant authorises one `watch`, a restart included, so every restart mints its own first.
  iceRestartHandler = async (feed: ControlledReceivingHandle) => {
    await refreshSubscribeGrant();

    // The mint can take seconds; a leave, reset or Janus hangup in that window owns the outcome.
    if (!this.restartStillWanted(feed)) {
      return;
    }

    this.restartWatchInFlight = true;

    try {
      await sendMessage(
        feed.janusPlugin,
        watchRestartTemplate(RTCClient.mountpointId!, RTCClient.roomPin!)
      );
    } catch (error) {
      this.restartWatchInFlight = false;

      if (!isSubscribeGrantRefusal(error)) {
        throw error;
      }

      this.refuseRestart(feed, 'core', SUBSCRIBE_GRANT_REFUSED_CODE);
    }
  };

  private restartStillWanted = (feed: ControlledReceivingHandle) =>
    this.negotiationMeta.iceRestartAttempt &&
    !RTCClient.supressErrors &&
    this.feed === RTCClient.receivingFeed &&
    this.plugin === feed &&
    !this.negotiationMeta.mediaFailureReported &&
    !!feed.janusPlugin.webrtcStuff?.pc;

  // The server keeps the PC but will not restart it, so no later rung can heal it.
  private refuseRestart = (
    feed: ControlledReceivingHandle,
    refusal: StreamingRestartRefusal,
    code: number | null
  ) => {
    if (!this.restartStillWanted(feed)) {
      return;
    }

    emitStreamingRestartRefused({ refusal, code, handle: feed.handle });
    this.reportMediaFailure(
      'connection_failed',
      feed.janusPlugin.webrtcStuff?.pc?.iceConnectionState ?? 'disconnected',
      { previouslyConnected: true }
    );
  };
}
